import { API_ENDPOINTS, apiService } from "../api";
import { isLiveApiFlag } from "../apiMode";
import { triggerMockStoryRefresh } from "./storyRefreshNowApi.mock";
import {
  mapStoryRefreshTriggerResponse,
  toStoryRefreshTriggerRequest,
} from "./storyRefreshNowMappers";
import type {
  StoryRefreshTriggerResponseApi,
  StoryRefreshTriggerResult,
} from "./storyRefreshNow.types";

/**
 * House convention (`services/apiMode.ts`): unset or `live` → real HTTP,
 * `mock` → offline fixtures. So this hits the network out of the box, exactly
 * like every other `REACT_APP_STORY_*_API` feature, and 404s until the backend
 * PR (doc-hub #46) deploys — which is the visible, debuggable failure we want,
 * not a silent fake success.
 *
 * Set `REACT_APP_STORY_REFRESH_NOW_API=mock` to work offline against
 * `storyRefreshNowApi.mock.ts`.
 */
const isLiveStoryRefreshNowApi = (): boolean =>
  isLiveApiFlag(process.env.REACT_APP_STORY_REFRESH_NOW_API);

/**
 * Trigger an on-demand refresh ("Refresh Now").
 *
 * Resolves on 202 — which the spec returns both for a newly queued refresh and
 * for an idempotent replay inside the 30-second debounce window; the two are
 * indistinguishable, and both mean "the user's click was accepted".
 *
 * Rejects on 403/404/409/422 with the backend's own message intact (the
 * `services/api.ts` interceptor unwraps it), so call sites must surface it via
 * `getApiErrorMessage` rather than substituting a fixed string.
 *
 * @param force Bypass both the debounce window and the in-progress check.
 */
export const triggerStoryRefresh = async (
  workspaceId: string,
  storyId: string,
  force = false,
): Promise<StoryRefreshTriggerResult> => {
  const response = isLiveStoryRefreshNowApi()
    ? await apiService.post<StoryRefreshTriggerResponseApi>(
        API_ENDPOINTS.stories.refreshNow(workspaceId, storyId),
        toStoryRefreshTriggerRequest(force),
      )
    : await triggerMockStoryRefresh(workspaceId, storyId, force);

  return mapStoryRefreshTriggerResponse(response);
};
